import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useActivityFeed } from "../hooks/useActivityFeed";
import { Sheet } from "./Sheet";

const ACTIVITY_SEEN_KEY = "wir_activity_last_seen";

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min.`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  return new Date(ms).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

// Quick-tap starters for when you want to send something but a blank
// textarea is intimidating — tapping appends rather than replaces, so one
// can be used as a starting point and still be built on.
const NOTE_STARTERS = ["Ich liebe dich ❤️", "Denk gerade an dich 🥰", "Du fehlst mir", "Danke, dass es dich gibt", "Du machst mich glücklich ✨", "Bis später, mein Schatz 💕"];

export function TopBar() {
  const [open, setOpen] = useState(false);
  const [noteSheetOpen, setNoteSheetOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSent, setNoteSent] = useState(false);
  const [noteSending, setNoteSending] = useState(false);
  const { user, logout } = useAuth();
  const { couple, partner, profile } = useCouple();
  const navigate = useNavigate();

  const activity = useActivityFeed(couple?.id ?? null, user?.uid ?? null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [lastSeenActivity, setLastSeenActivity] = useState(() => {
    try {
      return Number(localStorage.getItem(ACTIVITY_SEEN_KEY) ?? 0);
    } catch {
      return 0;
    }
  });
  const hasUnseenActivity = activity.some((item) => item.timestamp > lastSeenActivity);

  function openActivity() {
    setOpen(false);
    setActivityOpen(true);
    const now = Date.now();
    setLastSeenActivity(now);
    try {
      localStorage.setItem(ACTIVITY_SEEN_KEY, String(now));
    } catch {
      // Best-effort — worst case the dot reappears next load.
    }
  }

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate("/login");
  }

  function appendStarter(phrase: string) {
    setNoteText((prev) => (prev.trim() ? `${prev.trim()} ${phrase}` : phrase));
  }

  function openNoteSheet() {
    setOpen(false);
    setNoteText("");
    setNoteSent(false);
    setNoteSending(false);
    setNoteSheetOpen(true);
  }

  async function sendNote(e: FormEvent) {
    e.preventDefault();
    // Without this guard, a quick double-tap on "Senden" fires this twice
    // before the Sheet switches to the confirmation view — each is a real
    // write, and each separately triggers the partner's push notification.
    if (noteSending || !noteText.trim() || !couple || !partner || !user) return;
    setNoteSending(true);
    try {
      await setDoc(doc(db, "couples", couple.id, "loveNotes", partner.uid), {
        text: noteText.trim(),
        fromUid: user.uid,
        fromName: profile?.displayName ?? "",
        createdAt: serverTimestamp(),
      });
      setNoteSent(true);
    } finally {
      setNoteSending(false);
    }
  }

  return (
    <>
      {/* backdrop-blur below makes this element a containing block for any
          position:fixed descendant (same effect as `filter`/`transform`),
          so nothing meant to be fixed to the viewport (the dropdown, the
          Sheet) may live inside it — they're rendered as siblings instead. */}
      <header className="sticky top-0 z-30 border-b border-cream-200 bg-cream-50/95 backdrop-blur dark:border-night-border dark:bg-night-bg/95">
        <div className="mx-auto flex h-14 max-w-md items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-plum-700 text-cream-50">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                <path d="M12 21s-7.5-4.6-10.3-9.3C.1 9.1 1 5.6 4 4.3c2.3-1 4.7-.1 6 1.8l2 2.9 2-2.9c1.3-1.9 3.7-2.8 6-1.8 3 1.3 3.9 4.8 2.3 7.4C19.5 16.4 12 21 12 21Z" />
              </svg>
            </span>
            <span className="font-display text-lg font-semibold text-plum-900 dark:text-night-accent">Wir</span>
          </Link>

          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Neuigkeiten"
              onClick={openActivity}
              className="relative flex h-10 w-10 items-center justify-center rounded-lg text-ink-900 dark:text-night-text"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5.5 w-5.5">
                <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9Z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.5 17a2.5 2.5 0 0 0 5 0" strokeLinecap="round" />
              </svg>
              {hasUnseenActivity && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-garnet-600 ring-2 ring-cream-50 dark:ring-night-bg" />
              )}
            </button>
            <button
              type="button"
              aria-label={open ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={open}
              onClick={() => {
                setActivityOpen(false);
                setOpen((v) => !v);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-ink-900 dark:text-night-text"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-6 w-6">
                {open ? (
                  <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      {open && (
        <>
          <button
            aria-label="Menü schließen"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-20 bg-ink-900/20"
          />
          <nav
            className="fixed top-16 z-30 w-60 overflow-hidden rounded-2xl border border-cream-200 bg-white shadow-xl dark:border-night-border dark:bg-night-surface"
            style={{ right: "max(1rem, calc(50% - 224px + 1rem))" }}
          >
            <ul className="divide-y divide-cream-100 px-1.5 py-1.5 dark:divide-night-border">
              <MenuLink to="/savings" onClick={() => setOpen(false)}>
                Sparziele
              </MenuLink>
              <MenuLink to="/dates" onClick={() => setOpen(false)}>
                Wichtige Daten
              </MenuLink>
              <MenuLink to="/notes" onClick={() => setOpen(false)}>
                Notizen
              </MenuLink>
              <MenuLink to="/links" onClick={() => setOpen(false)}>
                Links
              </MenuLink>
              <MenuLink to="/mood" onClick={() => setOpen(false)}>
                Stimmung
              </MenuLink>
              {partner && (
                <li>
                  <button onClick={openNoteSheet} className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm text-ink-900 dark:text-night-text">
                    <span>💌</span> Kleine Nachricht
                  </button>
                </li>
              )}
              <MenuLink to="/notifications" onClick={() => setOpen(false)}>
                Benachrichtigungen
              </MenuLink>
              <MenuLink to="/account" onClick={() => setOpen(false)}>
                Konto
              </MenuLink>
              <li>
                <button onClick={handleLogout} className="flex w-full items-center rounded-xl px-3 py-2.5 text-left text-sm font-medium text-garnet-600">
                  Abmelden
                </button>
              </li>
            </ul>
          </nav>
        </>
      )}

      <Sheet
        open={noteSheetOpen}
        onClose={() => setNoteSheetOpen(false)}
        title={noteSent ? "Gesendet" : `Nachricht für ${partner?.displayName ?? "deine Partnerin/deinen Partner"}`}
      >
        {noteSent ? (
          <div className="py-2 text-center">
            <p className="text-3xl">💌</p>
            <p className="mt-2 text-sm text-ink-600 dark:text-night-muted">
              Wird {partner?.displayName} ganz sanft angezeigt, sobald die App als Nächstes geöffnet wird.
            </p>
            <button
              onClick={() => setNoteSheetOpen(false)}
              className="mt-4 h-11 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50"
            >
              Schließen
            </button>
          </div>
        ) : (
          <form onSubmit={sendNote} className="space-y-3">
            <textarea
              placeholder="z.B. Ich denke an dich, mein Schatz…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-cream-200 bg-white px-4 py-3 text-base outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
            <div className="flex flex-wrap gap-1.5">
              {NOTE_STARTERS.map((phrase) => (
                <button
                  key={phrase}
                  type="button"
                  onClick={() => appendStarter(phrase)}
                  className="rounded-full border border-cream-200 px-2.5 py-1 text-xs font-medium text-ink-600 dark:border-night-border dark:text-night-muted"
                >
                  {phrase}
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-400 dark:text-night-faint">
              {partner?.displayName} sieht das einmalig als kleine Überraschung beim nächsten Öffnen der App — du bekommst
              keine Bestätigung, wenn es gelesen wurde.
            </p>
            <button
              type="submit"
              disabled={!noteText.trim() || noteSending}
              className="h-11 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
            >
              {noteSending ? "Wird gesendet…" : "Senden"}
            </button>
          </form>
        )}
      </Sheet>

      {activityOpen && (
        <>
          <button
            aria-label="Neuigkeiten schließen"
            onClick={() => setActivityOpen(false)}
            className="fixed inset-0 z-20 bg-ink-900/20"
          />
          <nav
            className="fixed top-16 z-30 w-72 overflow-hidden rounded-2xl border border-cream-200 bg-white shadow-xl dark:border-night-border dark:bg-night-surface"
            style={{ right: "max(3.75rem, calc(50% - 224px + 3.75rem))" }}
          >
            {activity.length === 0 ? (
              <p className="px-4 py-4 text-sm text-ink-400 dark:text-night-faint">Noch nichts Neues.</p>
            ) : (
              <ul className="divide-y divide-cream-100 px-1.5 py-1.5 dark:divide-night-border">
                {activity.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.link}
                      onClick={() => setActivityOpen(false)}
                      className="flex items-center justify-between gap-3 rounded-xl px-2.5 py-2.5 text-left hover:bg-cream-100 dark:hover:bg-night-surface-raised"
                    >
                      <p className="min-w-0 truncate text-sm text-ink-900 dark:text-night-text">
                        <span className="font-semibold">{item.name}</span> {item.text}
                      </p>
                      <span className="flex-shrink-0 text-xs text-ink-400 dark:text-night-faint">{relativeTime(item.timestamp)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </nav>
        </>
      )}
    </>
  );
}

function MenuLink({ to, children, onClick }: { to: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <li>
      <Link to={to} onClick={onClick} className="flex items-center rounded-xl px-3 py-2.5 text-sm text-ink-900 dark:text-night-text">
        {children}
      </Link>
    </li>
  );
}
